import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Menu,
  DollarSign,
  Clock,
  Truck,
  FileText,
  Shield,
  Users,
  Globe,
  Bell,
  HelpCircle,
} from "lucide-react";

const faqs = [
  {
    question: "What is FullPlateNow?",
    answer:
      "FullPlateNow is a platform that helps restaurants manage their entire presence on major delivery services like DoorDash, Uber Eats, Grubhub, and Postmates from one dashboard. We handle pricing (including a 50-60% upcharge as our service fee, paid by customers), menu syncing, order management, analytics, and communication with delivery services, ensuring you keep 100% of your base menu prices with no delivery fees.",
    icon: <Menu className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "Do I need separate accounts for DoorDash, Uber Eats, etc.?",
    answer:
      "No. We connect to your existing accounts or help you create new ones during onboarding. Everything is managed through one dashboard, saving you time and effort.",
    icon: <Globe className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "How does FullPlateNow make money?",
    answer:
      "We manage pricing on delivery apps by applying a 50-60% upcharge to your base menu prices, which is paid by customers through the delivery services. For example, if your base price for an item is $10, we may set it at $15-$16, ensuring you receive the full $10 while we retain the difference as our service fee, typically $8-9 per $50 order.",
    icon: <DollarSign className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "How quickly do I get paid?",
    answer:
      "We issue direct deposits every week. You can track your earnings and payout status in your dashboard, ensuring transparency and timely payments.",
    icon: <Clock className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "Is there a contract or monthly fee?",
    answer:
      "No contracts, no subscriptions. You only pay when you make a sale through the platform, as our service fee is built into the upcharge on each order.",
    icon: <FileText className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "What happens if I need to update my menu?",
    answer:
      "You can update your menu directly in the FullPlateNow dashboard, and we’ll sync the changes across all connected delivery services, ensuring consistency without manual effort.",
    icon: <Menu className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "How does FullPlateNow handle order tracking?",
    answer:
      "We aggregate order statuses (e.g., preparing, in transit, delivered) from all delivery services into one dashboard, so you can monitor every order in real time without switching between apps.",
    icon: <Truck className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "Can I communicate with delivery drivers through the platform?",
    answer:
      "Yes, our platform facilitates communication with delivery service representatives or drivers, allowing you to address order issues or coordinate deliveries directly from your dashboard.",
    icon: <Users className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "What kind of analytics does FullPlateNow provide?",
    answer:
      "We offer daily, weekly, and monthly reports on order volume, revenue, and performance across all delivery platforms, helping you understand your business trends and make informed decisions.",
    icon: <FileText className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "How does FullPlateNow ensure my data is secure?",
    answer:
      "We implement industry-standard security measures, including encryption and access controls, to protect your business data. However, no system is entirely secure, and we’re not liable for breaches unless caused by our gross negligence.",
    icon: <Shield className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "Can I integrate FullPlateNow with my restaurant’s website?",
    answer:
      "Yes, we provide embeddable links or widgets for your website, directing orders to delivery services while tracking them through our platform for seamless management.",
    icon: <Globe className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "What happens if there’s an issue with a delivery service?",
    answer:
      "Our platform includes AI-driven support to automate 90% of inquiries, and you can communicate directly with delivery service representatives through our dashboard to resolve issues quickly.",
    icon: <HelpCircle className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "Will I get notifications for new orders or updates?",
    answer:
      "Yes, our notification system alerts you about new orders, driver updates, and messages from delivery services, ensuring you stay informed in real time.",
    icon: <Bell className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "Does FullPlateNow support restaurants in multiple cities?",
    answer:
      "Yes, we support multi-metro expansion, managing your operations across different locations seamlessly, whether you’re in one city or several.",
    icon: <Globe className="w-6 h-6 text-[#FF5A5F]" />,
  },
  {
    question: "How does FullPlateNow handle taxes on the upcharge?",
    answer:
      "We collect and remit applicable taxes on the upcharge service fee as required by law. You’re responsible for taxes on your base menu price earnings, and we provide reports to assist with tax compliance.",
    icon: <DollarSign className="w-6 h-6 text-[#FF5A5F]" />,
  },
];

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-gradient-to-b from-white to-[#FFF5F5] text-black font-sans px-6 py-16 max-w-4xl mx-auto">
      <h1 className="text-4xl font-bold mb-8 text-center">
        Frequently Asked Questions
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg p-4 cursor-pointer bg-white shadow-md hover:shadow-lg transition-all duration-300"
            onClick={() => toggle(index)}
          >
            <div className="flex items-center space-x-3">
              {faq.icon}
              <div className="flex-1 flex justify-between items-center">
                <h3 className="font-semibold text-lg">{faq.question}</h3>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-[#FF5A5F]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-black/60" />
                )}
              </div>
            </div>

            {openIndex === index && (
              <p className="text-sm text-black/80 mt-3">{faq.answer}</p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <p className="text-sm text-black/60">
          Still have questions?{" "}
          <a
            href="mailto:support@fullplatenow.com"
            className="text-[#FF5A5F] underline"
          >
            Contact our support team
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default FAQs;
